import {Controller} from "stimulus";
import omit from "lodash/omit";

type formDataObject = Record<string, string>;

const buildFormData = (form: HTMLFormElement): formDataObject => {
  const formData = new FormData(form);
  const keys: string[] = Array.from(formData.keys());
  const formObject = keys.reduce((acc, key) => ({
    ...acc,
    [key]: formData.getAll(key),
  }), {});

  return omit(formObject, ["authenticity_token"]);
};

const isEqual = (left: formDataObject, right: formDataObject) =>
  JSON.stringify(left) === JSON.stringify(right);

export default class ConfirmAbandonFormController extends Controller<HTMLFormElement> {
  static values = {confirmMessage: String};

  declare confirmMessageValue: string;
  declare initialFormDataValue: formDataObject;

  private currentUrl = "";

  connect() {
    this.currentUrl = window.location.href;
    this.initializeFormData();
    window.addEventListener("beforeunload", this.handleWindowLeave);
    document.addEventListener("turbo:before-visit", this.handleConfirm);
    document.addEventListener("turbo:before-render", this.handleBeforeRender);
    document.addEventListener("tlw-modal:close", this.handleConfirm);
    document.addEventListener("turbo:submit-end", this.initializeFormData);
    this.element.addEventListener("submit", this.initializeFormData);
  }

  disconnect() {
    window.removeEventListener("beforeunload", this.handleWindowLeave);
    document.removeEventListener("turbo:before-visit", this.handleConfirm);
    document.removeEventListener("turbo:before-render", this.handleBeforeRender);
    document.removeEventListener("tlw-modal:close", this.handleConfirm);
    document.removeEventListener("turbo:submit-end", this.initializeFormData);
    this.element.removeEventListener("submit", this.initializeFormData);
  }

  initializeFormData = () => {
    this.initialFormDataValue = buildFormData(this.element);
  };

  handleConfirm = (event) => {
    if (event.type === "tlw-modal:close" && !event.target.contains(this.element)) return;

    if (this.isFormDirty() && !window.confirm(this.confirmMessageValue)) {
      event.preventDefault();
    } else if (this.isFormDirty()) {
      // Reset form dirty state if confirmed exit but remained on same route
      this.initializeFormData();
    }
  };

  // Handles browser back/forward button navigation, which triggers a Turbo restoration
  // visit that does not fire turbo:before-visit. Restores the URL if the user cancels.
  handleBeforeRender = (event) => {
    if (!this.isFormDirty()) return;

    event.preventDefault();
    history.pushState(null, "", this.currentUrl);

    if (window.confirm(this.confirmMessageValue)) {
      this.initializeFormData();
      event.detail.resume();
    }
  };

  handleWindowLeave = (event) => {
    if (this.isFormDirty()) {
      event.preventDefault();
      return (event.returnValue = "");
    }
  };

  isFormDirty = () => {
    const currentFormDataValue = buildFormData(this.element);

    return !isEqual(this.initialFormDataValue, currentFormDataValue);
  };
}
